/**
 * useHistorischeDaten V4 — Dynamische Liquiditätsplanung
 * 
 * Erkennt automatisch alle vorhandenen Monate aus den Daten.
 * Unterstützt: SV Büro (Apr 2023-Dez 2023 + Apr 2025-Apr 2026), LCS, konsolidiert.
 * Prognose basiert auf den letzten 6 Monaten historischer Daten.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ============================================================
// TYPEN
// ============================================================

export interface MonatsDaten {
  monat: string;
  label: string;
  einnahmen: number;
  ausgaben: number;
  cashflow: number;
  istPrognose: boolean;
}

export interface TabellenZeile {
  id: string;
  label: string;
  typ: 'header' | 'einnahme' | 'ausgabe' | 'summe' | 'saldo' | 'kpi';
  level: number;
  icon?: string;
  farbe?: string;
  werte: Record<string, number>;
  children?: TabellenZeile[];
  isEditable?: boolean;
  parentId?: string;
  anzahlChildren?: number;
}

export interface MitarbeiterKosten {
  id: string;
  firma: string;
  name: string;
  position: string;
  typ: string;
  brutto_gehalt: number;
  ag_anteil_sv: number;
  sonstige_kosten: number;
  gesamt_kosten: number;
  aktiv: boolean;
  notiz?: string;
}

export interface PlanWert {
  id?: string;
  firma: string;
  kategorie: string;
  unterkategorie?: string;
  typ: 'einnahme' | 'ausgabe';
  monat: string;
  betrag: number;
  notiz?: string;
  ist_fix: boolean;
}

export interface LiquiditaetStats {
  gesamtEinnahmen: number;
  gesamtAusgaben: number;
  gesamtCashflow: number;
  avgEinnahmen: number;
  avgAusgaben: number;
  avgCashflow: number;
  monate: number;
  firmen: string[];
  zeitraumVon: string;
  zeitraumBis: string;
  personalKosten: number;
  offeneKlaerungen: number;
}

// Kategorie-Labels
export const KATEGORIE_LABELS: Record<string, string> = {
  // Einnahmen
  einnahmen_anwaelte: 'Anwälte / Regulierung',
  einnahmen_versicherung: 'Versicherungs-Regulierung',
  einnahmen_gutachten: 'Gutachten-Honorare',
  einnahmen_sonstige: 'Sonstige Einnahmen',
  versicherung_betrieblich: 'Versicherungs-Regulierung (Betrieblich)',
  anwalt_regulierung: 'Anwälte / Regulierung',
  gutachten_zahlung: 'Gutachten-Zahlungen',
  buero_versand: 'Büro / Versand',
  // Ausgaben — Steuern detailliert
  steuern: 'Steuern (Gesamt)',
  steuern_ust: 'Umsatzsteuer (USt)',
  steuern_est: 'Einkommensteuer (ESt)',
  steuern_kst: 'Körperschaftsteuer (KSt)',
  steuern_lst: 'Lohnsteuer (LSt)',
  steuern_gewst: 'Gewerbesteuer (GewSt)',
  steuern_sonstige: 'Sonstige Steuern & Abgaben',
  steuern_abgaben: 'Steuern / Abgaben',
  // Ausgaben — Personal
  personal: 'Personal & Gehälter',
  sozialversicherung: 'Sozialversicherung',
  gehalt: 'Gehälter / Löhne',
  // Ausgaben — Betrieb
  zulieferer: 'Zulieferer & Material',
  zulieferer_material: 'Zulieferer / Material',
  versicherungen: 'Versicherungsprämien',
  versicherung: 'Versicherungen',
  beratung: 'Steuerberater & Beratung',
  steuerberater: 'Steuerberater',
  it_software: 'IT, Software & Telekom',
  software_it: 'Software / IT',
  software: 'Software / IT',
  software_it_telekom: 'Software / IT / Telekom',
  telekommunikation: 'Telekommunikation',
  betriebskosten: 'Miete, Energie & Büro',
  miete: 'Miete',
  miete_energie: 'Miete / Energie',
  energie: 'Energie / Strom / Gas',
  fahrzeuge: 'Fahrzeuge & Kraftstoff',
  fahrzeug: 'Fahrzeug / KFZ',
  fahrzeug_kfz: 'Fahrzeug / KFZ',
  fahrzeug_leasing: 'Fahrzeug-Leasing',
  fahrzeug_kraftstoff: 'Kraftstoff',
  kraftstoff: 'Kraftstoff',
  tankstelle: 'Kraftstoff / Tanken',
  marketing: 'Werbung & Marketing',
  werbung_marketing: 'Werbung / Marketing',
  bankkosten: 'Bankgebühren',
  bankgebuehren: 'Bankgebühren',
  finanzierung: 'Leasing & Finanzierung',
  leasing_kredit: 'Leasing / Kredit',
  ausgaben_anwaelte: 'Eigene Anwaltskosten',
  ausgaben_sonstige: 'Sonstige Ausgaben',
  intern: 'Interne Verrechnung',
  intern_lcs: 'Interne Verrechnung (LCS)',
  intern_privat: 'Privat-Entnahmen/-Einlagen',
  interne_verrechnung: 'Interne Verrechnung',
  provision: 'Provisionen',
  sonstiges: 'Sonstiges',
  dat_lizenz: 'DAT-Lizenzen',
  post_gutschrift: 'Post / Versand',
  buero_material: 'Büromaterial',
  lastschrift: 'Lastschriften',
  ueberweisung: 'Überweisungen',
  unbekannt: 'Noch nicht zugeordnet',
  frage: 'Klärungsbedarf',
  unklar: 'Noch nicht zugeordnet',
};

export const KATEGORIE_FARBEN: Record<string, string> = {
  steuern: '#ef4444', steuern_ust: '#ef4444', steuern_est: '#dc2626', steuern_kst: '#b91c1c',
  steuern_lst: '#f87171', steuern_gewst: '#fca5a5', steuern_sonstige: '#fecaca',
  personal: '#f97316', sozialversicherung: '#fb923c', gehalt: '#fdba74',
  zulieferer: '#eab308', zulieferer_material: '#eab308',
  versicherungen: '#84cc16', versicherung: '#84cc16', versicherung_betrieblich: '#14b8a6',
  beratung: '#22c55e', steuerberater: '#22c55e',
  it_software: '#06b6d4', software_it: '#06b6d4', software: '#06b6d4', telekommunikation: '#0891b2',
  betriebskosten: '#3b82f6', miete: '#3b82f6', energie: '#60a5fa',
  fahrzeuge: '#8b5cf6', fahrzeug: '#8b5cf6', fahrzeug_leasing: '#a78bfa', fahrzeug_kraftstoff: '#c4b5fd',
  marketing: '#ec4899', werbung_marketing: '#ec4899',
  bankkosten: '#6b7280', bankgebuehren: '#6b7280',
  finanzierung: '#f59e0b', leasing_kredit: '#f59e0b',
  ausgaben_anwaelte: '#dc2626', ausgaben_sonstige: '#9ca3af',
  einnahmen_anwaelte: '#10b981', einnahmen_versicherung: '#14b8a6',
  einnahmen_gutachten: '#0ea5e9', einnahmen_sonstige: '#a78bfa',
  intern: '#d1d5db', intern_lcs: '#d1d5db', intern_privat: '#e5e7eb',
  buero_material: '#94a3b8', buero_versand: '#94a3b8',
};

const FIRMA_LABELS: Record<string, string> = {
  sv_buero_koop: 'SV Büro Koop',
  lcs: 'LCS GmbH',
  lcs_gmbh: 'LCS GmbH',
};

// ============================================================
// HELPER
// ============================================================

export function formatMonatLabel(monat: string): string {
  const [year, month] = monat.split('-');
  const labels = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return `${labels[parseInt(month) - 1]} ${year.slice(2)}`;
}

/**
 * Dynamisch: Erkennt alle Monate aus den Daten + 6 Prognose-Monate
 */
function generateDynamischeMonate(transaktionen: any[]): { alleMonate: string[]; historischeMonate: string[]; prognoseMonate: string[] } {
  // Alle Monate aus den Daten sammeln
  const monateSet = new Set<string>();
  for (const tx of transaktionen) {
    const datum = tx.buchung_datum || '';
    const monat = datum.substring(0, 7);
    if (monat && monat.length >= 7) monateSet.add(monat);
  }

  const historischeMonate = Array.from(monateSet).sort();
  
  if (historischeMonate.length === 0) {
    return { alleMonate: [], historischeMonate: [], prognoseMonate: [] };
  }

  // 6 Prognose-Monate nach dem letzten historischen Monat
  const letzterMonat = historischeMonate[historischeMonate.length - 1];
  const [lastY, lastM] = letzterMonat.split('-').map(Number);
  const prognoseMonate: string[] = [];
  for (let i = 1; i <= 6; i++) {
    const d = new Date(lastY, lastM - 1 + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    prognoseMonate.push(key);
  }

  return {
    alleMonate: [...historischeMonate, ...prognoseMonate],
    historischeMonate,
    prognoseMonate
  };
}

// ============================================================
// HOOK
// ============================================================

export function useHistorischeDaten(selectedFirma: string = 'alle') {
  const [monatsDaten, setMonatsDaten] = useState<MonatsDaten[]>([]);
  const [tabellenZeilen, setTabellenZeilen] = useState<TabellenZeile[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<MitarbeiterKosten[]>([]);
  const [planWerte, setPlanWerte] = useState<PlanWert[]>([]);
  const [stats, setStats] = useState<LiquiditaetStats>({
    gesamtEinnahmen: 0, gesamtAusgaben: 0, gesamtCashflow: 0,
    avgEinnahmen: 0, avgAusgaben: 0, avgCashflow: 0,
    monate: 0, firmen: [], zeitraumVon: '', zeitraumBis: '',
    personalKosten: 0, offeneKlaerungen: 0
  });
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Alle geklärten Transaktionen laden (paginiert)
      let alleTx: any[] = [];
      let offset = 0;
      const batchSize = 1000;

      while (true) {
        let query = supabase
          .from('import_transaktionen')
          .select('buchung_datum, betrag, firma, kategorie, unterkategorie, klaerung_kategorie, status, beschreibung')
          .in('status', ['sicher', 'geklaert'])
          .order('buchung_datum', { ascending: true })
          .range(offset, offset + batchSize - 1);

        if (selectedFirma !== 'alle') {
          query = query.eq('firma', selectedFirma);
        }

        const { data, error } = await query;
        if (error) { console.error('Laden-Fehler:', error); break; }
        if (!data || data.length === 0) break;
        alleTx = alleTx.concat(data);
        if (data.length < batchSize) break;
        offset += batchSize;
      }

      // Offene Klärungen zählen
      let unkQuery = supabase
        .from('import_transaktionen')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'unklar');
      if (selectedFirma !== 'alle') unkQuery = unkQuery.eq('firma', selectedFirma);
      const { count: offeneCount } = await unkQuery;

      // 2. Mitarbeiter laden
      let maQuery = supabase.from('mitarbeiter_kosten').select('*').eq('aktiv', true);
      if (selectedFirma !== 'alle') maQuery = maQuery.eq('firma', selectedFirma);
      const { data: maData } = await maQuery;
      setMitarbeiter(maData || []);

      // 3. Planwerte laden
      let planQuery = supabase.from('liquiditaet_planwerte').select('*');
      if (selectedFirma !== 'alle') planQuery = planQuery.eq('firma', selectedFirma);
      const { data: planData } = await planQuery;
      setPlanWerte(planData || []);

      // 4. Dynamische Monate erkennen
      const { alleMonate, historischeMonate, prognoseMonate } = generateDynamischeMonate(alleTx);
      
      if (alleMonate.length === 0) {
        setMonatsDaten([]);
        setTabellenZeilen([]);
        setStats({
          gesamtEinnahmen: 0, gesamtAusgaben: 0, gesamtCashflow: 0,
          avgEinnahmen: 0, avgAusgaben: 0, avgCashflow: 0,
          monate: 0, firmen: [], zeitraumVon: '', zeitraumBis: '',
          personalKosten: 0, offeneKlaerungen: offeneCount || 0
        });
        setLoading(false);
        return;
      }

      const firmenSet = new Set<string>();

      // Monat → Kategorie → { betrag, anzahl, unterkat → { betrag, anzahl } }
      type KatData = { betrag: number; anzahl: number; unterkat: Record<string, { betrag: number; anzahl: number }> };
      const monatKatMap: Record<string, Record<string, KatData>> = {};

      for (const tx of alleTx) {
        const datum = tx.buchung_datum || '';
        const monat = datum.substring(0, 7);
        if (!monat || monat.length < 7) continue;
        firmenSet.add(tx.firma);

        if (!monatKatMap[monat]) monatKatMap[monat] = {};
        const kat = tx.klaerung_kategorie || tx.kategorie || 'sonstiges';
        const unterkat = tx.unterkategorie || '';
        const betrag = Number(tx.betrag) || 0;

        if (!monatKatMap[monat][kat]) {
          monatKatMap[monat][kat] = { betrag: 0, anzahl: 0, unterkat: {} };
        }
        monatKatMap[monat][kat].betrag += betrag;
        monatKatMap[monat][kat].anzahl += 1;

        if (unterkat) {
          if (!monatKatMap[monat][kat].unterkat[unterkat]) {
            monatKatMap[monat][kat].unterkat[unterkat] = { betrag: 0, anzahl: 0 };
          }
          monatKatMap[monat][kat].unterkat[unterkat].betrag += betrag;
          monatKatMap[monat][kat].unterkat[unterkat].anzahl += 1;
        }
      }

      // Durchschnitte für Prognose — basierend auf den letzten 6 historischen Monaten
      const letzte6 = historischeMonate.slice(-6);
      const histCount = letzte6.filter(m => monatKatMap[m]).length || 1;
      const katAvg: Record<string, { avg: number; unterkat: Record<string, number> }> = {};

      for (const monat of letzte6) {
        const kats = monatKatMap[monat];
        if (!kats) continue;
        for (const [kat, data] of Object.entries(kats)) {
          if (!katAvg[kat]) katAvg[kat] = { avg: 0, unterkat: {} };
          katAvg[kat].avg += data.betrag / histCount;
          for (const [uk, ukData] of Object.entries(data.unterkat)) {
            if (!katAvg[kat].unterkat[uk]) katAvg[kat].unterkat[uk] = 0;
            katAvg[kat].unterkat[uk] += ukData.betrag / histCount;
          }
        }
      }

      // MonatsDaten aufbauen
      const progMonateSet = new Set(prognoseMonate);
      const monatsDatenArr: MonatsDaten[] = alleMonate.map(monat => {
        const istPrognose = progMonateSet.has(monat);
        let einnahmen = 0;
        let ausgaben = 0;

        if (!istPrognose && monatKatMap[monat]) {
          for (const data of Object.values(monatKatMap[monat])) {
            if (data.betrag > 0) einnahmen += data.betrag;
            else ausgaben += Math.abs(data.betrag);
          }
        } else if (istPrognose) {
          for (const data of Object.values(katAvg)) {
            if (data.avg > 0) einnahmen += data.avg;
            else ausgaben += Math.abs(data.avg);
          }
        }

        return { monat, label: formatMonatLabel(monat), einnahmen, ausgaben, cashflow: einnahmen - ausgaben, istPrognose };
      });
      setMonatsDaten(monatsDatenArr);

      // Stats
      const histDaten = monatsDatenArr.filter(m => !m.istPrognose && (m.einnahmen > 0 || m.ausgaben > 0));
      const gesamtE = histDaten.reduce((s, m) => s + m.einnahmen, 0);
      const gesamtA = histDaten.reduce((s, m) => s + m.ausgaben, 0);
      const personalK = (maData || []).reduce((s: number, m: any) => s + (m.gesamt_kosten || m.brutto_gehalt + m.ag_anteil_sv + (m.sonstige_kosten || 0)), 0);
      
      setStats({
        gesamtEinnahmen: gesamtE,
        gesamtAusgaben: gesamtA,
        gesamtCashflow: gesamtE - gesamtA,
        avgEinnahmen: gesamtE / (histDaten.length || 1),
        avgAusgaben: gesamtA / (histDaten.length || 1),
        avgCashflow: (gesamtE - gesamtA) / (histDaten.length || 1),
        monate: histDaten.length,
        firmen: Array.from(firmenSet),
        zeitraumVon: histDaten[0]?.monat || '',
        zeitraumBis: histDaten[histDaten.length - 1]?.monat || '',
        personalKosten: personalK,
        offeneKlaerungen: offeneCount || 0
      });

      // 5. AGICAP-STYLE TABELLENZEILEN
      const zeilen: TabellenZeile[] = [];

      // --- Anfangssaldo ---
      let laufenderSaldo = 0;
      const anfangsSaldoW: Record<string, number> = {};
      alleMonate.forEach(m => {
        anfangsSaldoW[m] = laufenderSaldo;
        const md = monatsDatenArr.find(d => d.monat === m);
        if (md) laufenderSaldo += md.cashflow;
      });
      zeilen.push({ id: 'saldo_anfang', label: 'Liquidität zu Monatsbeginn', typ: 'saldo', level: 0, icon: 'Wallet', farbe: '#3b82f6', werte: anfangsSaldoW });

      // --- EINNAHMEN ---
      const einnahmenW: Record<string, number> = {};
      alleMonate.forEach(m => { einnahmenW[m] = monatsDatenArr.find(d => d.monat === m)?.einnahmen || 0; });

      const einnahmenKats = new Map<string, { unterKats: Map<string, true> }>();
      for (const [monat, kats] of Object.entries(monatKatMap)) {
        for (const [kat, data] of Object.entries(kats)) {
          if (data.betrag > 0) {
            if (!einnahmenKats.has(kat)) einnahmenKats.set(kat, { unterKats: new Map() });
            for (const uk of Object.keys(data.unterkat)) {
              if (data.unterkat[uk].betrag > 0) einnahmenKats.get(kat)!.unterKats.set(uk, true);
            }
          }
        }
      }

      const einnahmenChildren: TabellenZeile[] = [];
      const sortedEinKats = Array.from(einnahmenKats.entries()).sort((a, b) => {
        const sumA = historischeMonate.reduce((s, m) => s + (monatKatMap[m]?.[a[0]]?.betrag > 0 ? monatKatMap[m][a[0]].betrag : 0), 0);
        const sumB = historischeMonate.reduce((s, m) => s + (monatKatMap[m]?.[b[0]]?.betrag > 0 ? monatKatMap[m][b[0]].betrag : 0), 0);
        return sumB - sumA;
      });

      for (const [kat, info] of sortedEinKats) {
        const katW: Record<string, number> = {};
        alleMonate.forEach(m => {
          const v = monatKatMap[m]?.[kat]?.betrag;
          katW[m] = (v && v > 0) ? v : (progMonateSet.has(m) && katAvg[kat]?.avg > 0 ? katAvg[kat].avg : 0);
        });

        const unterChildren: TabellenZeile[] = [];
        for (const uk of Array.from(info.unterKats.keys())) {
          const ukW: Record<string, number> = {};
          alleMonate.forEach(m => {
            const v = monatKatMap[m]?.[kat]?.unterkat?.[uk]?.betrag;
            ukW[m] = (v && v > 0) ? v : (progMonateSet.has(m) && katAvg[kat]?.unterkat?.[uk] > 0 ? katAvg[kat].unterkat[uk] : 0);
          });
          unterChildren.push({ id: `ein_${kat}_${uk}`, label: KATEGORIE_LABELS[uk] || uk, typ: 'einnahme', level: 2, werte: ukW, isEditable: true, parentId: `ein_${kat}` });
        }

        einnahmenChildren.push({
          id: `ein_${kat}`, label: KATEGORIE_LABELS[kat] || kat, typ: 'einnahme', level: 1,
          werte: katW, children: unterChildren.length > 0 ? unterChildren : undefined,
          parentId: 'einnahmen', anzahlChildren: unterChildren.length
        });
      }

      zeilen.push({
        id: 'einnahmen', label: 'Einzahlungen', typ: 'einnahme', level: 0,
        icon: 'ArrowUpRight', farbe: '#10b981', werte: einnahmenW,
        children: einnahmenChildren, anzahlChildren: einnahmenChildren.length
      });

      // --- AUSGABEN ---
      const ausgabenW: Record<string, number> = {};
      alleMonate.forEach(m => { ausgabenW[m] = monatsDatenArr.find(d => d.monat === m)?.ausgaben || 0; });

      const ausgabenKats = new Map<string, { unterKats: Map<string, true> }>();
      for (const [monat, kats] of Object.entries(monatKatMap)) {
        for (const [kat, data] of Object.entries(kats)) {
          if (data.betrag < 0) {
            if (!ausgabenKats.has(kat)) ausgabenKats.set(kat, { unterKats: new Map() });
            for (const uk of Object.keys(data.unterkat)) {
              if (data.unterkat[uk].betrag < 0) ausgabenKats.get(kat)!.unterKats.set(uk, true);
            }
          }
        }
      }

      const ausgabenChildren: TabellenZeile[] = [];
      const sortedAusKats = Array.from(ausgabenKats.entries()).sort((a, b) => {
        const sumA = historischeMonate.reduce((s, m) => s + Math.abs(monatKatMap[m]?.[a[0]]?.betrag < 0 ? monatKatMap[m][a[0]].betrag : 0), 0);
        const sumB = historischeMonate.reduce((s, m) => s + Math.abs(monatKatMap[m]?.[b[0]]?.betrag < 0 ? monatKatMap[m][b[0]].betrag : 0), 0);
        return sumB - sumA;
      });

      for (const [kat, info] of sortedAusKats) {
        const katW: Record<string, number> = {};
        alleMonate.forEach(m => {
          const v = monatKatMap[m]?.[kat]?.betrag;
          katW[m] = (v && v < 0) ? Math.abs(v) : (progMonateSet.has(m) && katAvg[kat]?.avg < 0 ? Math.abs(katAvg[kat].avg) : 0);
        });

        const unterChildren: TabellenZeile[] = [];

        // Mitarbeiter unter Personal einfügen
        if (kat.includes('sozialversicherung') || kat.includes('personal') || kat === 'gehalt') {
          for (const ma of (maData || [])) {
            const maW: Record<string, number> = {};
            const kosten = ma.gesamt_kosten || (ma.brutto_gehalt + ma.ag_anteil_sv + (ma.sonstige_kosten || 0));
            alleMonate.forEach(m => { maW[m] = kosten; });
            unterChildren.push({
              id: `ma_${ma.id}`, label: `${ma.name} (${ma.position || ma.typ})`,
              typ: 'ausgabe', level: 2, werte: maW, isEditable: true, parentId: `aus_${kat}`
            });
          }
        }

        for (const uk of Array.from(info.unterKats.keys())) {
          const ukW: Record<string, number> = {};
          alleMonate.forEach(m => {
            const v = monatKatMap[m]?.[kat]?.unterkat?.[uk]?.betrag;
            ukW[m] = (v && v < 0) ? Math.abs(v) : (progMonateSet.has(m) && katAvg[kat]?.unterkat?.[uk] < 0 ? Math.abs(katAvg[kat].unterkat[uk]) : 0);
          });
          unterChildren.push({ id: `aus_${kat}_${uk}`, label: KATEGORIE_LABELS[uk] || uk, typ: 'ausgabe', level: 2, werte: ukW, isEditable: true, parentId: `aus_${kat}` });
        }

        ausgabenChildren.push({
          id: `aus_${kat}`, label: KATEGORIE_LABELS[kat] || kat, typ: 'ausgabe', level: 1,
          werte: katW, children: unterChildren.length > 0 ? unterChildren : undefined,
          parentId: 'ausgaben', anzahlChildren: unterChildren.length
        });
      }

      zeilen.push({
        id: 'ausgaben', label: 'Auszahlungen', typ: 'ausgabe', level: 0,
        icon: 'ArrowDownRight', farbe: '#ef4444', werte: ausgabenW,
        children: ausgabenChildren, anzahlChildren: ausgabenChildren.length
      });

      // --- CASHFLOW ---
      const cashflowW: Record<string, number> = {};
      alleMonate.forEach(m => { cashflowW[m] = (einnahmenW[m] || 0) - (ausgabenW[m] || 0); });
      zeilen.push({ id: 'cashflow', label: 'Cashflow (Ein − Aus)', typ: 'summe', level: 0, icon: 'Activity', farbe: '#8b5cf6', werte: cashflowW });

      // --- ENDSALDO ---
      const endSaldoW: Record<string, number> = {};
      alleMonate.forEach(m => { endSaldoW[m] = (anfangsSaldoW[m] || 0) + (cashflowW[m] || 0); });
      zeilen.push({ id: 'saldo_ende', label: 'Liquidität zum Monatsende', typ: 'saldo', level: 0, icon: 'TrendingUp', farbe: '#3b82f6', werte: endSaldoW });

      // --- KPIs ---
      const burnRateW: Record<string, number> = {};
      const runwayW: Record<string, number> = {};
      alleMonate.forEach(m => {
        burnRateW[m] = ausgabenW[m] || 0;
        const saldo = endSaldoW[m] || 0;
        const burn = ausgabenW[m] || 1;
        runwayW[m] = Math.max(0, Math.round(saldo / burn * 10) / 10);
      });
      zeilen.push({ id: 'burn_rate', label: 'Burn Rate', typ: 'kpi', level: 0, icon: 'Flame', farbe: '#f97316', werte: burnRateW });
      zeilen.push({ id: 'runway', label: 'Runway (Monate)', typ: 'kpi', level: 0, icon: 'Timer', farbe: '#06b6d4', werte: runwayW });

      setTabellenZeilen(zeilen);

    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFirma]);

  // Zeile auf-/zuklappen
  const toggleRow = useCallback((rowId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  // Planwert speichern
  const savePlanWert = useCallback(async (planwert: PlanWert) => {
    try {
      if (planwert.id) {
        await supabase.from('liquiditaet_planwerte').update({
          betrag: planwert.betrag, notiz: planwert.notiz, updated_at: new Date().toISOString()
        }).eq('id', planwert.id);
      } else {
        await supabase.from('liquiditaet_planwerte').insert({
          firma: planwert.firma, kategorie: planwert.kategorie,
          unterkategorie: planwert.unterkategorie, typ: planwert.typ,
          monat: planwert.monat, betrag: planwert.betrag,
          notiz: planwert.notiz, ist_fix: planwert.ist_fix
        });
      }
      await loadData();
    } catch (err) { console.error('Planwert-Fehler:', err); }
  }, [loadData]);

  // Mitarbeiter CRUD
  const saveMitarbeiter = useCallback(async (ma: Partial<MitarbeiterKosten>) => {
    try {
      if (ma.id) {
        const { id, gesamt_kosten, ...rest } = ma;
        await supabase.from('mitarbeiter_kosten').update({ ...rest, updated_at: new Date().toISOString() }).eq('id', id);
      } else {
        const { id, gesamt_kosten, ...rest } = ma;
        await supabase.from('mitarbeiter_kosten').insert(rest);
      }
      await loadData();
    } catch (err) { console.error('Mitarbeiter-Fehler:', err); }
  }, [loadData]);

  const deleteMitarbeiter = useCallback(async (id: string) => {
    try {
      await supabase.from('mitarbeiter_kosten').delete().eq('id', id);
      await loadData();
    } catch (err) { console.error('Mitarbeiter-Löschen-Fehler:', err); }
  }, [loadData]);

  useEffect(() => { loadData(); }, [loadData]);

  return {
    monatsDaten, tabellenZeilen, mitarbeiter, planWerte, stats, loading,
    expandedRows, toggleRow, savePlanWert, saveMitarbeiter, deleteMitarbeiter,
    reload: loadData, formatMonatLabel
  };
}
